import { useOffersQuery, useAcceptOfferMutation, useDeclineOfferMutation } from "@/hooks/api/offer.queries";
import OfferCard from "@/components/OfferCard";
import { Spinner } from "@/components";
import { Sparkles, Briefcase, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import PageContainer from "@/components/PageContainer";
import { OfferType } from "@/services/offer.service";

const OffersPage = () => {
  const { data: offers = [], isLoading, isError } = useOffersQuery();
  const acceptMutation = useAcceptOfferMutation();
  const declineMutation = useDeclineOfferMutation();

  const handleAccept = (id: string) => {
    acceptMutation.mutate(id);
  };

  const handleDecline = (id: string) => {
    declineMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <PageContainer className="flex h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="h-12 w-12 text-indigo-600" />
          <p className="text-slate-500 font-bold animate-pulse text-sm">Loading your future opportunities...</p>
        </div>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer className="p-8">
        <Alert variant="destructive" className="rounded-2xl border-rose-100 bg-rose-50 text-rose-800">
          <AlertTitle className="font-black flex items-center gap-2">
             <Info size={18} /> Error Fetching Offers
          </AlertTitle>
          <AlertDescription>
            There was a problem loading your internship offers. Please try again later or contact support.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="p-8 space-y-10 min-h-screen relative overflow-hidden">
      {/* Decorative Blob */}
      <div className="absolute top-0 right-0 -z-10 h-[400px] w-[400px] bg-indigo-500/5 blur-[100px] rounded-full pointer-events-none" />

      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-100">
            <Sparkles size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Internship Offers</h1>
            <p className="text-slate-500 font-medium text-sm">Review and respond to your placement opportunities</p>
          </div>
        </div>
      </header>

      {offers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom-5 duration-700">
          {offers.map((offer: OfferType) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              onAccept={handleAccept}
              onDecline={handleDecline}
              isAccepting={acceptMutation.isPending && acceptMutation.variables === offer.id}
              isDeclining={declineMutation.isPending && declineMutation.variables === offer.id}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-[50vh] text-center space-y-6 animate-in zoom-in-95 duration-500">
          <div className="w-24 h-24 rounded-[2.5rem] bg-slate-50 flex items-center justify-center text-slate-300 shadow-inner">
            <Briefcase size={48} />
          </div>
          <div className="space-y-2 max-w-sm">
            <h2 className="text-xl font-black text-slate-700">No offers yet</h2>
            <p className="text-slate-400 text-sm font-medium">
              Keep applying! Great things take time. Check back later or explore new internships.
            </p>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default OffersPage;
